import { Trans, useTranslation } from 'react-i18next';
import { faExternalLinkAlt } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { UserIcon } from '@framework/ui/atoms/UserIcon';
import { generateDefaultIcon } from '@framework/utils';
import type { GroupMemberInvitation } from '@group/domain';
import { useLoginErrorMessage } from '@user/Login';

type ExternalLinkProps = { href: string; children?: React.ReactNode };
const ExternalLink: React.FC<ExternalLinkProps> = ({ href, children }: ExternalLinkProps) => (
  <a href={href} target="_blank" rel="noreferrer" className="text-blue-700 underline hover:text-blue-800">
    {children} <FontAwesomeIcon icon={faExternalLinkAlt} />
  </a>
);

type Props = {
  invitation: GroupMemberInvitation;
  children: React.ReactNode;
};

export const InvitationMenuLayout: React.FC<Props> = ({ invitation, children }: Props) => {
  const { t } = useTranslation();
  const { senderGroup, senderUser } = invitation;
  const loginErrorMessage = useLoginErrorMessage();

  return (
    <div className="flex flex-col items-center py-4">
      <div>
        <UserIcon
          name={senderUser.name}
          id={senderUser.userId}
          imageUrl={senderUser.imageUrl}
          isAnonymous={false}
          size="small"
          className="inline-block align-middle leading-6"
        />
        {t('auth.invitation.senderIntro', { name: senderUser.name, email: senderUser.email })}
      </div>

      {loginErrorMessage && <div className="py-4 text-red-700">{loginErrorMessage}</div>}

      <div className="flex flex-row items-center py-4">
        <div className="block size-10 overflow-hidden rounded-full border border-gray-600">
          {senderGroup.imageUrl ? (
            <img src={senderGroup.imageUrl} alt={senderGroup.name} className="size-full object-cover" />
          ) : (
            <div className="size-full object-cover">
              {generateDefaultIcon({ id: senderGroup.groupId, name: senderGroup.name, size: 40 })}
            </div>
          )}
        </div>
        <div className="pl-2 text-2xl font-bold">{senderGroup.name}</div>
      </div>

      {children}

      <div className="pt-8 text-sm text-gray-700">
        <Trans
          i18nKey="auth.signUp.terms"
          components={{
            termsLink: <ExternalLink href="https://levii.co.jp/legal/terms-of-service/" />,
            privacyLink: <ExternalLink href="https://levii.co.jp/legal/privacy-policy/" />,
          }}
        />
      </div>
    </div>
  );
};
